/**
 * The search box at the top of the library.
 *
 * Typing searches; there is no button to press and nowhere else to go. What
 * is typed goes into the address after a short pause (a search per keystroke
 * is a billed request per keystroke), replacing the entry it came from, so
 * Back leaves the search in one step and not a letter at a time.
 */
import { useEffect, useId, useState } from 'react';
import { useDebounced } from '../../search/useDebounced';
import { TEXT_MAX } from './libraryQuery';
import styles from './Library.module.css';

export function LibrarySearchBox({ value, onChange, autoFocus = false }: {
  /** What the address says is being searched for. */
  value: string;
  onChange: (text: string) => void;
  autoFocus?: boolean;
}) {
  const id = useId();
  const [typed, setTyped] = useState(value);
  // The address changed under us (Back, a link, "Clear search"): show what it says.
  const [seen, setSeen] = useState(value);
  if (value !== seen) { setSeen(value); setTyped(value); }

  const settled = useDebounced(typed, 250);
  useEffect(() => {
    if (settled.trim() !== value.trim() && settled === typed) onChange(settled);
    // Only when typing settles: `value` and `onChange` change with every answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  return (
    <div className={styles.search} role="search">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.7" />
        <path d="M11 11l3.2 3.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
      <label className="vh" htmlFor={id}>Search policies</label>
      <input
        id={id}
        type="search"
        className={styles.searchInput}
        autoComplete="off"
        spellCheck={false}
        maxLength={TEXT_MAX}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder="Search every policy, including document text"
      />
      {typed && (
        <button type="button" className={styles.searchClear} onClick={() => { setTyped(''); onChange(''); }}>
          Clear search
        </button>
      )}
    </div>
  );
}
